import { getStyleGuideWordLimit, validateStyleGuideLength, styleGuideSaveErrorMessage } from "../../../../../src/shared/styleGuideWordLimit";
import { composeSimpleStyleInstructions, normalizeSimpleStyleControls, type SimpleStyleControls } from "../../../../../src/shared/simpleStyleControls";
import React, { type ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { DismissableLayer } from "@radix-ui/react-dismissable-layer";
import InlineSVG from "react-inlinesvg";
import styled, { createGlobalStyle } from "styled-components/macro";
import Auth from "src/Auth";
import { SudoMenuButton, MenuPopoverItem } from "src/sudokit/components/SudoMenuButton";
import { MenuDeleteConfirmation } from "src/sudokit/components/SudoMenuButton/MenuDeleteConfirmation";
import { AddNewStyleModal } from "src/components/StyleGuide/AddNewStyleModal";
import { popInKeyframes } from "src/components/AnimatedWrapper/styles";
import { readGeneratedSampleStorage, writeGeneratedSampleStorage } from "src/components/StyleGuide/generatedSampleStorage";
import { StyleEditorSurface, type StyleEditorTab } from "src/components/StyleGuide/StyleEditorSurface";
import StyleMatcher from "src/components/StoryBible/StyleMatcher";
import { useToast } from "src/components/ToastWrapper";
import { useProjectStore } from "src/stores/projectStore";
import { useSudowriteStore } from "src/stores/sudowriteStore";
import { useStoryBibleStore } from "src/stores/storyBibleStore";
import useWriteModel from "src/hooks/useWriteModel";
import { DEFAULT_WRITE_MODEL } from "../../../../../src/shared/constants.js";
import {
  DEFAULT_STYLE_GUIDE_GENERATE_SAMPLE_WRITE_MODEL,
  STYLE_GUIDE_GENERATE_SAMPLE_MODEL_OPTIONS,
  normalizeStyleGuideGenerateSampleWriteModel,
} from "../../../../../src/shared/styleGuideGenerateSampleModels.js";
import { STORY_BIBLE_STYLE_ID, STORY_BIBLE_STYLE_NAME, STYLE_GUIDE_BUILT_IN_STYLES } from "../../../../../src/shared/styleGuideBuiltInStyles.js";

const ASSET_ROOT = "/assets/style-guide/settings";
const STYLE_GUIDE_STYLES_CHANGED_EVENT = "style-guide-styles-changed";
// Room below the final row for its two-action menu and shadow.
const STYLE_ROW_MENU_BOTTOM_SPACE = 96;
const SETTINGS_MANAGER_STYLE_ORDER = [
  "action-packed",
  "approachable-charming",
  "dark-foreboding",
  "refined-literary",
  "sultry",
  "gritty",
];

async function copyTextToClipboard(text: string): Promise<boolean> {
  if (!text || typeof navigator === "undefined" || !navigator.clipboard?.writeText) return false;
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

type StyleView = "list" | "blank" | "featured";
type StyleSource = "story_bible" | "sudowrite" | "user_style";

interface StylesManagerSectionProps {
  onClose: () => void;
}

interface StyleListItem {
  source: StyleSource;
  id: string;
  name: string;
  tag?: string;
  kind?: string;
  description?: string;
  instructions?: string;
  writingSample?: string;
  simpleControls?: SimpleStyleControls;
  hidden?: boolean;
}

const EMPTY_STYLE: StyleListItem = {
  source: "user_style",
  id: "",
  name: "Untitled Style",
  tag: "Custom",
  kind: "blank",
  instructions: "",
  writingSample: "",
  hidden: false,
};

const DEFAULT_STORY_BIBLE_STYLE: StyleListItem = {
  source: "story_bible",
  id: STORY_BIBLE_STYLE_ID,
  name: STORY_BIBLE_STYLE_NAME,
  kind: "story_bible",
  description: "Uses the project's current Story Bible style guidance.",
  instructions: "",
  writingSample: "",
  hidden: false,
};

const DEFAULT_BUILT_IN_STYLES: StyleListItem[] = STYLE_GUIDE_BUILT_IN_STYLES.map((style: any) => ({
  source: "sudowrite",
  id: style.id,
  name: style.name,
  tag: style.tag,
  kind: "sudowrite",
  description: style.description,
  instructions: style.promptStyleText,
  writingSample: style.writingSample ?? "",
  hidden: false,
}));

const DEFAULT_SELECTED_STYLE = {
  source: "story_bible" as const,
  id: STORY_BIBLE_STYLE_ID,
  name: STORY_BIBLE_STYLE_NAME,
};

function emitStyleGuideStylesChanged(projectId?: string) {
  if (!projectId || typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(STYLE_GUIDE_STYLES_CHANGED_EVENT, { detail: { projectId } }));
}

function ensureStyleRows(styles: StyleListItem[]): StyleListItem[] {
  const existingBuiltInIds = new Set(styles.filter((style) => style.source === "sudowrite").map((style) => style.id));
  const incomingStoryBibleStyle = styles.find((style) => style.source === "story_bible") ?? DEFAULT_STORY_BIBLE_STYLE;
  const { tag: _storyBibleTag, ...storyBibleStyle } = incomingStoryBibleStyle;
  return [
    storyBibleStyle,
    ...styles.filter((style) => style.source !== "story_bible"),
    ...DEFAULT_BUILT_IN_STYLES.filter((style) => !existingBuiltInIds.has(style.id)),
  ];
}

const slugifyStyleToken = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const styleRowIdentityKey = (row: StyleListItem) => `${row.source}:${row.id || row.name}`;

const styleRowTestId = (row: StyleListItem) => `styles-manager-row-${slugifyStyleToken(styleRowIdentityKey(row))}`;

const isEditableStyleListRow = (row: StyleListItem) =>
  (row.source === "user_style" || row.source === "story_bible") && Boolean(row.id);

export function StylesManagerSection({ onClose }: StylesManagerSectionProps) {
  const project = useProjectStore((state: any) => state.project);
  const projectId = project?.id as string | undefined;
  const styleSettingsFocusStyleId = useSudowriteStore((state: any) => state.styleSettingsFocusStyleId);
  const clearStyleSettingsFocusStyle = useSudowriteStore((state: any) => state.clearStyleSettingsFocusStyle);
  const editorOptions = useSudowriteStore((state) => state.editorOptions);
  const currentWriteModel = useWriteModel({ editorOptions });
  const toast = useToast();
  const [view, setView] = useState<StyleView>("list");
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [rowMenuStyleKey, setRowMenuStyleKey] = useState<string | null>(null);
  const [pendingDeleteStyle, setPendingDeleteStyle] = useState<StyleListItem | null>(null);
  const [generateStyleModalOpen, setGenerateStyleModalOpen] = useState(false);
  const [styleMatcherOpen, setStyleMatcherOpen] = useState(false);
  const [styleMatcherInitialSample, setStyleMatcherInitialSample] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [styles, setStyles] = useState<StyleListItem[]>([]);
  const [stylesLoadState, setStylesLoadState] = useState<"loading" | "loaded" | "error">("loading");
  const [selectedStyle, setSelectedStyle] = useState<{ source: StyleSource; id: string; name: string } | null>(null);
  const [activeStyle, setActiveStyle] = useState<StyleListItem>(EMPTY_STYLE);
  const [styleNameDraft, setStyleNameDraft] = useState("");
  const styleNameDraftRef = useRef("");
  const [activeDetailTab, setActiveDetailTab] = useState<StyleEditorTab>("foundation");
  const [simpleControls, setSimpleControls] = useState<SimpleStyleControls>({});
  const simpleControlsRef = useRef<SimpleStyleControls>({});
  const simpleControlsEditedRef = useRef(false);
  const styleSaveQueueRef = useRef<Promise<void>>(Promise.resolve());
  const [instructionsDraft, setInstructionsDraft] = useState("");
  const instructionsDraftRef = useRef("");
  const detailMainRef = useRef<HTMLDivElement | null>(null);
  const detailInstructionsSectionRef = useRef<HTMLDivElement | null>(null);
  const detailWritingSampleSectionRef = useRef<HTMLDivElement | null>(null);
  const instructionsTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [generatedSamplesByStyleKey, setGeneratedSamplesByStyleKey] = useState<Record<string, string>>(() =>
    readGeneratedSampleStorage(projectId),
  );
  const [isGeneratingSample, setIsGeneratingSample] = useState(false);
  const [generateSampleError, setGenerateSampleError] = useState("");
  const [generateSampleWriteModel, setGenerateSampleWriteModel] = useState(DEFAULT_STYLE_GUIDE_GENERATE_SAMPLE_WRITE_MODEL);
  const generateSampleWriteModelManuallySelectedRef = useRef(false);
  const [isSavingStyle, setIsSavingStyle] = useState(false);
  const [saveStyleError, setSaveStyleError] = useState("");
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const searchButtonRef = useRef<HTMLButtonElement | null>(null);
  const searchWasOpenRef = useRef(false);
  const addMenuRootRef = useRef<HTMLDivElement | null>(null);
  const activeStyleKey = activeStyle.id ? styleRowIdentityKey(activeStyle) : "";
  const generatedSample = activeStyleKey ? generatedSamplesByStyleKey[activeStyleKey] ?? "" : "";
  const defaultGenerateSampleWriteModel = normalizeStyleGuideGenerateSampleWriteModel(currentWriteModel)
    || normalizeStyleGuideGenerateSampleWriteModel(DEFAULT_WRITE_MODEL)
    || DEFAULT_STYLE_GUIDE_GENERATE_SAMPLE_WRITE_MODEL;
  const mergeStylesListResponseRef = useRef<(data: any, options?: { tryFocus?: boolean; initializeActiveStyle?: boolean }) => void>(() => undefined);
  const openFocusedStyleEditorRef = useRef<(focusedStyle: StyleListItem) => void>(() => undefined);
  const focusRefetchAttemptedRef = useRef<string | null>(null);

  const resetGenerateSampleWriteModel = () => {
    generateSampleWriteModelManuallySelectedRef.current = false;
    setGenerateSampleWriteModel(defaultGenerateSampleWriteModel);
  };

  const updateGenerateSampleWriteModel = (writeModel: string) => {
    generateSampleWriteModelManuallySelectedRef.current = true;
    setGenerateSampleWriteModel(writeModel);
  };

  useEffect(() => {
    if (!projectId) {
      setStylesLoadState("loaded");
      setStyles([]);
      setSelectedStyle(null);
      setActiveStyle(EMPTY_STYLE);
      setStyleNameDraft("");
      styleNameDraftRef.current = "";
      setActiveDetailTab("foundation");
      setInstructionsDraft("");
      instructionsDraftRef.current = "";
      return undefined;
    }

    let cancelled = false;
    setStylesLoadState("loading");
    Auth.fetchWithIdToken("/api/style-settings/styles/list", { projectId })
      .then((data: any) => {
        if (cancelled) return;
        mergeStylesListResponseRef.current(data, {
          tryFocus: Boolean(useSudowriteStore.getState().styleSettingsFocusStyleId),
          initializeActiveStyle: true,
        });
      })
      .catch(() => {
        if (cancelled) return;
        setStylesLoadState("error");
        setStyles([DEFAULT_STORY_BIBLE_STYLE, ...DEFAULT_BUILT_IN_STYLES]);
        setSelectedStyle(DEFAULT_SELECTED_STYLE);
        setActiveStyle(EMPTY_STYLE);
        setStyleNameDraft("");
        styleNameDraftRef.current = "";
        setActiveDetailTab("foundation");
        setInstructionsDraft("");
        instructionsDraftRef.current = "";
        if (instructionsTextareaRef.current) {
          instructionsTextareaRef.current.value = "";
        }
      });

    return () => {
      cancelled = true;
    };
  }, [projectId]);

  useEffect(() => {
    if (view !== "blank" || generateSampleWriteModelManuallySelectedRef.current) return;
    setGenerateSampleWriteModel(defaultGenerateSampleWriteModel);
  }, [defaultGenerateSampleWriteModel, view]);

  useEffect(() => {
    if (!styleSettingsFocusStyleId || stylesLoadState !== "loaded" || !projectId) return;
    const focusedStyle = styles.find(
      (style) => style.source === "user_style" && style.id === styleSettingsFocusStyleId,
    );
    if (focusedStyle) {
      focusRefetchAttemptedRef.current = null;
      openFocusedStyleEditorRef.current(focusedStyle);
      return;
    }

    if (focusRefetchAttemptedRef.current === styleSettingsFocusStyleId) {
      return;
    }
    focusRefetchAttemptedRef.current = styleSettingsFocusStyleId;

    let cancelled = false;
    Auth.fetchWithIdToken("/api/style-settings/styles/list", { projectId })
      .then((data: any) => {
        if (cancelled) return;
        mergeStylesListResponseRef.current(data, { tryFocus: true });
      })
      .catch(() => {
        if (cancelled) return;
        focusRefetchAttemptedRef.current = null;
      });

    return () => {
      cancelled = true;
    };
  }, [projectId, styleSettingsFocusStyleId, styles, stylesLoadState]);

  useEffect(() => {
    if (!projectId) return undefined;

    const handleStylesChanged = (event: Event) => {
      const changedProjectId = (event as CustomEvent<{ projectId?: string }>).detail?.projectId;
      if (changedProjectId !== projectId) return;

      Auth.fetchWithIdToken("/api/style-settings/styles/list", { projectId })
        .then((data: any) => {
          mergeStylesListResponseRef.current(data, {
            tryFocus: Boolean(useSudowriteStore.getState().styleSettingsFocusStyleId),
          });
        })
        .catch(() => undefined);
    };

    window.addEventListener(STYLE_GUIDE_STYLES_CHANGED_EVENT, handleStylesChanged);
    return () => {
      window.removeEventListener(STYLE_GUIDE_STYLES_CHANGED_EVENT, handleStylesChanged);
    };
  }, [projectId]);

  useEffect(() => {
    setGeneratedSamplesByStyleKey(readGeneratedSampleStorage(projectId));
  }, [projectId]);

  useEffect(() => {
    writeGeneratedSampleStorage(projectId, generatedSamplesByStyleKey);
  }, [generatedSamplesByStyleKey, projectId]);

  const rows = useMemo(
    () =>
      [...styles].sort((first, second) => {
        if (first.source === "story_bible" || second.source === "story_bible") return first.source === "story_bible" ? -1 : 1;
        if (first.source !== second.source) return first.source === "user_style" ? -1 : 1;
        if (first.source === "user_style") return first.name.localeCompare(second.name);
        const firstIndex = SETTINGS_MANAGER_STYLE_ORDER.indexOf(first.id);
        const secondIndex = SETTINGS_MANAGER_STYLE_ORDER.indexOf(second.id);
        return (firstIndex === -1 ? Number.MAX_SAFE_INTEGER : firstIndex) -
          (secondIndex === -1 ? Number.MAX_SAFE_INTEGER : secondIndex);
      }),
    [styles],
  );

  const filteredRows = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    if (!normalizedQuery) return rows;
    return rows.filter((row) => row.name.toLowerCase().includes(normalizedQuery));
  }, [rows, searchQuery]);

  useLayoutEffect(() => {
    if (searchOpen) {
      searchInputRef.current?.focus({ preventScroll: true });
    } else if (searchWasOpenRef.current) {
      searchButtonRef.current?.focus({ preventScroll: true });
    }
    searchWasOpenRef.current = searchOpen;
  }, [searchOpen]);

  const openBlankStyle = async () => {
    if (!projectId) return;
    setAddMenuOpen(false);
    setRowMenuStyleKey(null);
    setGenerateStyleModalOpen(false);
    setStyleMatcherOpen(false);
    setSearchOpen(false);
    setSearchQuery("");
    try {
      const data = await Auth.fetchWithIdToken("/api/style-settings/v2/styles/create", {
        projectId,
        surface: "settings",
        source: "blank",
        name: "Untitled Style",
        instructions: "",
        writingSample: "",
      });
      const createdStyle = data?.style;
      if (createdStyle) {
        setStyles((current) => [createdStyle, ...current]);
        setSelectedStyle(data?.selectedStyle ?? { source: "user_style", id: createdStyle.id, name: createdStyle.name });
        setActiveStyle(createdStyle);
        setStyleNameDraftValue(createdStyle.name);
        setActiveDetailTab("foundation");
        setInstructionsDraftValue(createdStyle.instructions ?? "", { preserveGeneratedSample: true });
        resetGenerateSampleWriteModel();
        emitStyleGuideStylesChanged(projectId);
      }
      setView("blank");
    } catch (error) {
      toast?.setToast?.("Failed to create style.");
    }
  };

  const openGenerateStyle = () => {
    setAddMenuOpen(false);
    setRowMenuStyleKey(null);
    setSearchOpen(false);
    setSearchQuery("");
    setStyleMatcherInitialSample("");
    setGenerateStyleModalOpen(true);
  };

  const openSearch = () => {
    setAddMenuOpen(false);
    setRowMenuStyleKey(null);
    setSearchOpen(true);
  };

  const closeSearch = () => {
    setSearchQuery("");
    setSearchOpen(false);
  };

  const clearOrCloseSearch = () => {
    if (searchQuery) {
      setSearchQuery("");
      searchInputRef.current?.focus();
      return;
    }
    closeSearch();
  };

  const setInstructionsDraftValue = (value: string, { preserveGeneratedSample = false }: { preserveGeneratedSample?: boolean } = {}) => {
    instructionsDraftRef.current = value;
    setInstructionsDraft(value);
    if (!preserveGeneratedSample && activeStyleKey) {
      setGeneratedSamplesByStyleKey((current) => {
        if (!current[activeStyleKey]) return current;
        const { [activeStyleKey]: _removed, ...rest } = current;
        return rest;
      });
    }
    setGenerateSampleError("");
    setSaveStyleError("");
  };

  const setStyleNameDraftValue = (value: string) => {
    styleNameDraftRef.current = value;
    setStyleNameDraft(value);
    setSaveStyleError("");
  };

  const applyStyleEditorState = (
    style: StyleListItem,
    { openDetail = false }: { openDetail?: boolean } = {},
  ) => {
    setActiveStyle(style);
    const controls = normalizeSimpleStyleControls(style.simpleControls);
    simpleControlsRef.current = controls;
    simpleControlsEditedRef.current = false;
    setSimpleControls(controls);
    setStyleNameDraftValue(style.name);
    setActiveDetailTab(style.source === "sudowrite" ? "instructions" : "foundation");
    setInstructionsDraftValue(style.instructions ?? "", { preserveGeneratedSample: true });
    if (instructionsTextareaRef.current) {
      instructionsTextareaRef.current.value = style.instructions ?? "";
    }
    resetGenerateSampleWriteModel();
    if (openDetail) {
      setView(style.source === "user_style" || style.source === "story_bible" ? "blank" : "featured");
    }
  };

  const openStyleRow = (row: StyleListItem) => {
    setSearchOpen(false);
    setSearchQuery("");
    applyStyleEditorState(row, { openDetail: true });
  };

  const openFocusedStyleEditor = (focusedStyle: StyleListItem) => {
    setAddMenuOpen(false);
    setRowMenuStyleKey(null);
    setGenerateStyleModalOpen(false);
    setStyleMatcherOpen(false);
    setSearchOpen(false);
    setSearchQuery("");
    applyStyleEditorState(focusedStyle, { openDetail: true });
    focusRefetchAttemptedRef.current = null;
    clearStyleSettingsFocusStyle?.();
  };

  openFocusedStyleEditorRef.current = openFocusedStyleEditor;
  mergeStylesListResponseRef.current = (data, { tryFocus = false, initializeActiveStyle = false } = {}) => {
    const nextStyles = ensureStyleRows(Array.isArray(data?.styles) ? data.styles : []);
    setStylesLoadState("loaded");
    setStyles(nextStyles);
    setSelectedStyle(data?.selectedStyle ?? null);

    const focusId = tryFocus ? useSudowriteStore.getState().styleSettingsFocusStyleId : null;
    const focusedStyle = focusId
      ? nextStyles.find((style) => style.source === "user_style" && style.id === focusId)
      : null;
    if (focusedStyle) {
      openFocusedStyleEditor(focusedStyle);
      return;
    }

    if (tryFocus && focusId) {
      return;
    }

    if (initializeActiveStyle) {
      const nextActiveStyle = nextStyles.find((style) => style.source === "user_style") ?? EMPTY_STYLE;
      applyStyleEditorState(nextActiveStyle);
    }
  };

  useEffect(() => {
    const controls = normalizeSimpleStyleControls(activeStyle.simpleControls);
    simpleControlsRef.current = controls;
    simpleControlsEditedRef.current = false;
    setSimpleControls(controls);
    // Only reset the draft when opening a different style. An autosave response
    // can contain older controls than the user's current unsaved selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStyle.id, activeStyle.source]);

  const getCurrentInstructionsDraft = () => instructionsTextareaRef.current?.value ?? instructionsDraftRef.current;
  const getCurrentStyleNameDraft = () => styleNameDraftRef.current;

  const scrollToDetailSection = (section: StyleEditorTab) => {
    setActiveDetailTab(section);
    if (section === "foundation" || section === "limits") return;
    const main = detailMainRef.current;
    const target = section === "instructions" ? detailInstructionsSectionRef.current : detailWritingSampleSectionRef.current;
    if (!main || !target) return;

    const nextTop = Math.max(0, target.getBoundingClientRect().top - main.getBoundingClientRect().top + main.scrollTop - 24);
    if (typeof main.scrollTo === "function") {
      main.scrollTo({ top: nextTop, behavior: "auto" });
      return;
    }
    main.scrollTop = nextTop;
  };

  const saveStyleName = async (nextNameValue = getCurrentStyleNameDraft()) => {
    if (!activeStyle.id || activeStyle.source !== "user_style") return;

    const nextName = nextNameValue.trim() || "Untitled Style";
    if (nextName === activeStyle.name) {
      setStyleNameDraftValue(nextName);
      return;
    }

    const data = await Auth.fetchWithIdToken("/api/style-settings/v2/styles/update", {
      id: activeStyle.id,
      projectId,
      surface: "settings",
      name: nextName,
    });
    const savedStyle = data?.style ?? { ...activeStyle, name: nextName };
    setActiveStyle(savedStyle);
    setStyles((current) => current.map((style) => (style.source === "user_style" && style.id === savedStyle.id ? savedStyle : style)));
    setSelectedStyle((current) =>
      current?.source === "user_style" && current.id === savedStyle.id
        ? { source: "user_style", id: savedStyle.id, name: savedStyle.name }
        : current,
    );
    setStyleNameDraftValue(savedStyle.name);
    emitStyleGuideStylesChanged(projectId);
  };

  const persistInstructions = async (
    nextStyle = getCurrentInstructionsDraft(),
    { syncLocalState = true, controls = simpleControlsRef.current, includeControls = false }: { syncLocalState?: boolean; controls?: SimpleStyleControls; includeControls?: boolean } = {},
  ) => {
    if (!activeStyle.id) return;
    validateStyleGuideLength(nextStyle, activeStyle.instructions ?? "");

    if (activeStyle.source === "story_bible") {
      const data = await Auth.fetchWithIdToken("/api/style-settings/v2/styles/update-story-bible", {
        projectId,
        surface: "settings",
        instructions: nextStyle,
        ...(includeControls || Object.keys(controls).length || Object.keys(normalizeSimpleStyleControls(activeStyle.simpleControls)).length ? { simpleControls: controls } : {}),
      });
      const savedStyle = data?.style ?? { ...activeStyle, instructions: nextStyle };
      setActiveStyle(savedStyle);
      setStyles((current) => current.map((style) => (style.source === "story_bible" ? savedStyle : style)));
      if (syncLocalState && instructionsDraftRef.current === nextStyle) {
        setInstructionsDraftValue(savedStyle?.instructions ?? nextStyle, { preserveGeneratedSample: true });
      }
      emitStyleGuideStylesChanged(projectId);
      return;
    }

    if (activeStyle.source !== "user_style") return;

    const data = await Auth.fetchWithIdToken("/api/style-settings/v2/styles/update", {
      id: activeStyle.id,
      projectId,
      surface: "settings",
      instructions: nextStyle,
      ...(includeControls || Object.keys(controls).length || Object.keys(normalizeSimpleStyleControls(activeStyle.simpleControls)).length ? { simpleControls: controls } : {}),
    });
    const savedStyle = data?.style ?? { ...activeStyle, instructions: nextStyle };
    setActiveStyle(savedStyle);
    setStyles((current) => current.map((style) => (style.source === "user_style" && style.id === savedStyle.id ? savedStyle : style)));
    if (syncLocalState && instructionsDraftRef.current === nextStyle) {
      setInstructionsDraftValue(savedStyle?.instructions ?? nextStyle, { preserveGeneratedSample: true });
    }
    emitStyleGuideStylesChanged(projectId);
  };

  const saveInstructions = (nextStyle = getCurrentInstructionsDraft(), options: { syncLocalState?: boolean; controls?: SimpleStyleControls } = {}) => {
    const controls = options.controls ?? simpleControlsRef.current;
    // Capture whether controls were edited even when the snapshot is all Off.
    // An earlier queued save may still be enabling them on the server.
    const includeControls = simpleControlsEditedRef.current;
    const next = styleSaveQueueRef.current.catch(() => undefined).then(() => persistInstructions(nextStyle, { ...options, controls, includeControls }));
    styleSaveQueueRef.current = next;
    return next;
  };

  const generateSamplePreview = async () => {
    const currentInstructions = getCurrentInstructionsDraft().trim();
    const sampleStyleKey = activeStyleKey;
    if (!composeSimpleStyleInstructions(currentInstructions, simpleControlsRef.current) || !sampleStyleKey) return;

    setIsGeneratingSample(true);
    setGenerateSampleError("");
    setGeneratedSamplesByStyleKey((current) => {
      if (!current[sampleStyleKey]) return current;
      const { [sampleStyleKey]: _removed, ...rest } = current;
      return rest;
    });
    try {
      const data = await Auth.fetchWithIdToken("/api/ai/storyEngine/style/generate-sample", {
        projectId,
        storyBibleActive: useStoryBibleStore.getState().isActive,
        instructions: currentInstructions,
        ...(Object.keys(simpleControlsRef.current).length ? { simpleControls: simpleControlsRef.current } : {}),
        writeModel: generateSampleWriteModel,
      });
      const sample = typeof data?.sample === "string" ? data.sample.trim() : "";
      if (!sample) {
        throw new Error("Generated sample was empty");
      }
      setGeneratedSamplesByStyleKey((current) => ({ ...current, [sampleStyleKey]: sample }));
    } catch (error) {
      setGenerateSampleError("Failed to generate a sample. Try again.");
    } finally {
      setIsGeneratingSample(false);
    }
  };

  const copyInstructions = () => {
    return copyTextToClipboard(getCurrentInstructionsDraft()).then((copied) => {
      toast?.setToast?.(copied ? "Instructions copied." : "Unable to copy instructions.", { layer: "modal" });
      return copied;
    });
  };

  const saveStyleDrafts = async ({ syncInstructionsLocalState = true }: { syncInstructionsLocalState?: boolean } = {}) => {
    setIsSavingStyle(true);
    setSaveStyleError("");
    try {
      await saveInstructions(getCurrentInstructionsDraft(), { syncLocalState: syncInstructionsLocalState });
    } catch (error) {
      const message = styleGuideSaveErrorMessage(error);
      setSaveStyleError(message);
      toast?.setToast?.(message);
      throw error;
    } finally {
      setIsSavingStyle(false);
    }
  };

  const saveSafely = (saveOperation: Promise<void>) => {
    void saveOperation.catch((error) => {
      const message = styleGuideSaveErrorMessage(error);
      setSaveStyleError(message);
      toast?.setToast?.(message);
    });
  };

  const saveInstructionsAndBack = async () => {
    try {
      await saveStyleDrafts();
      setView("list");
    } catch {
      // Keep the editor open so the user can retry without losing edits.
    }
  };

  const saveInstructionsAndClose = async () => {
    try {
      await saveStyleDrafts({ syncInstructionsLocalState: false });
      onClose();
    } catch {
      // Keep the editor open so the user can retry without losing edits.
    }
  };

  const saveGeneratedStyle = async (
    nextStyle: string,
    options?: { writingSample?: string; name?: string },
  ) => {
    if (!projectId) return;
    try {
      const createPayload: Record<string, string> = {
        projectId,
        surface: "settings",
        source: "generated",
        name: options?.name?.trim() || "Generated Style",
        instructions: nextStyle,
      };
      if (options?.writingSample) {
        createPayload.writingSample = options.writingSample;
      }
      const data = await Auth.fetchWithIdToken("/api/style-settings/v2/styles/create", createPayload);
      const createdStyle = data?.style;
      if (createdStyle) {
        setStyles((current) => [createdStyle, ...current]);
        setSelectedStyle(data?.selectedStyle ?? { source: "user_style", id: createdStyle.id, name: createdStyle.name });
        setAddMenuOpen(false);
        setRowMenuStyleKey(null);
        setGenerateStyleModalOpen(false);
        setStyleMatcherOpen(false);
        setSearchOpen(false);
        setSearchQuery("");
        setActiveStyle(createdStyle);
        setStyleNameDraftValue(createdStyle.name);
        setActiveDetailTab("foundation");
        setInstructionsDraftValue(createdStyle.instructions ?? "", { preserveGeneratedSample: true });
        resetGenerateSampleWriteModel();
        setView("blank");
        emitStyleGuideStylesChanged(projectId);
      }
    } catch (error) {
      toast?.setToast?.(styleGuideSaveErrorMessage(error, "Failed to create generated style."));
      throw error;
    }
  };

  const duplicateStyle = async (style: StyleListItem) => {
    if (!projectId) return;

    try {
      const data = await Auth.fetchWithIdToken("/api/style-settings/v2/styles/duplicate", {
        projectId,
        surface: "settings",
        source: style.source,
        id: style.id,
      });
      const duplicatedStyle = data?.style;
      if (duplicatedStyle) {
        setStyles((current) => [duplicatedStyle, ...current]);
        setSearchOpen(false);
        setSearchQuery("");
        emitStyleGuideStylesChanged(projectId);
      }
      setRowMenuStyleKey(null);
    } catch (error) {
      toast?.setToast?.(styleGuideSaveErrorMessage(error, "Failed to duplicate style."));
    }
  };

  const deleteStyle = async (style: StyleListItem) => {
    if (!projectId || style.source !== "user_style") return;

    try {
      const data = await Auth.fetchWithIdToken("/api/style-settings/styles/delete", {
        projectId,
        id: style.id,
        surface: "settings",
      });
      setStyles((current) => current.filter((row) => !(row.source === "user_style" && row.id === style.id)));
      if (data?.selectedStyle) {
        setSelectedStyle(data.selectedStyle);
      }
      setRowMenuStyleKey(null);
      if (activeStyle.id === style.id) {
        setActiveStyle(EMPTY_STYLE);
        setStyleNameDraftValue("");
        setView("list");
      }
      emitStyleGuideStylesChanged(projectId);
    } catch (error) {
      toast?.setToast?.("Failed to delete style.");
    }
  };

  const confirmDeleteStyle = () => {
    if (!pendingDeleteStyle) return;
    const style = pendingDeleteStyle;
    setPendingDeleteStyle(null);
    setRowMenuStyleKey(null);
    void deleteStyle(style);
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;

      if (addMenuOpen) {
        event.preventDefault();
        setAddMenuOpen(false);
        return;
      }

      if (view === "blank") {
        event.preventDefault();
        void saveInstructionsAndBack();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [addMenuOpen, saveInstructionsAndBack, view]);

  useEffect(() => {
    if (!addMenuOpen) return undefined;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (addMenuRootRef.current?.contains(target)) {
        return;
      }
      setAddMenuOpen(false);
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [addMenuOpen]);

  const renderStyleDetail = (content: ReactNode) => {
    const settingsDialogRoot =
      typeof document === "undefined"
        ? null
        : document.querySelector<HTMLElement>("[data-style-settings-dialog]");

    return settingsDialogRoot ? ReactDOM.createPortal(content, settingsDialogRoot) : content;
  };

  const toggleAddMenu = () => {
    setRowMenuStyleKey(null);
    setAddMenuOpen((open) => !open);
  };

  const renderStyleRows = () =>
    filteredRows.map((row) => {
      const rowIdentityKey = styleRowIdentityKey(row);
      return (
      <StyleManagerRow
        key={rowIdentityKey}
        role="button"
        tabIndex={0}
        aria-label={row.name}
        data-testid={styleRowTestId(row)}
        onClick={() => openStyleRow(row)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openStyleRow(row);
          }
        }}
      >
        <RowName>{row.name}</RowName>
        {row.tag && row.tag !== "Custom" && <SudowriteTag>{row.tag}</SudowriteTag>}
        <RowActions
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.stopPropagation();
            }
          }}
        >
          {isEditableStyleListRow(row) && (
            <RowEditButton type="button" aria-label={`Edit ${row.name}`} onClick={() => openStyleRow(row)}>
              <InlineSVG src={`${ASSET_ROOT}/settings-pencil-24.svg`} aria-hidden="true" />
            </RowEditButton>
          )}
          <SudoMenuButton
            modal={false}
            open={rowMenuStyleKey === rowIdentityKey}
            onOpenChange={(open) => {
              setRowMenuStyleKey(open ? rowIdentityKey : null);
              setPendingDeleteStyle(null);
              if (open) setAddMenuOpen(false);
            }}
            trigger={
              <RowIconButton type="button" aria-label={`More actions for ${row.name}`}>
                <InlineSVG src={`${ASSET_ROOT}/settings-dot-menu-24.svg`} aria-hidden="true" />
              </RowIconButton>
            }
            side="bottom"
            align="end"
            sideOffset={4}
            collisionPadding={8}
            menuAriaLabel={`Actions for style "${row.name}"`}
            menuPortalClassName="z-[1000000000]"
          >
            <div data-testid="styles-manager-row-menu">
              {pendingDeleteStyle ? (
                <MenuDeleteConfirmation onConfirm={confirmDeleteStyle}>
                  This will delete “{pendingDeleteStyle.name}” from your saved styles.
                </MenuDeleteConfirmation>
              ) : (
                <>
                  <MenuPopoverItem onSelect={() => void duplicateStyle(row)} closeOnSelect={false}>
                    <InlineSVG src="/assets/duplicate.svg" aria-hidden="true" />
                    <span>Duplicate</span>
                  </MenuPopoverItem>
                  {row.source === "user_style" && (
                    <MenuPopoverItem variant="destructive" closeOnSelect={false} onSelect={() => setPendingDeleteStyle(row)}>
                      <InlineSVG src={`${ASSET_ROOT}/settings-delete-24.svg`} aria-hidden="true" />
                      <span>Delete</span>
                    </MenuPopoverItem>
                  )}
                </>
              )}
            </div>
          </SudoMenuButton>
        </RowActions>
      </StyleManagerRow>
      );
    });

  const renderStylesTableContent = () => {
    if (stylesLoadState === "loading") {
      return <StylesTableStatus role="status">Loading styles...</StylesTableStatus>;
    }

    if (filteredRows.length === 0) {
      return (
        <StylesTableStatus role="status">
          {searchQuery.trim() ? "No styles match your search." : "No styles yet."}
        </StylesTableStatus>
      );
    }

    return renderStyleRows();
  };

  if (view === "blank") {
    return renderStyleDetail(
      <StyleEditorSurface
        mode="embedded"
        testId="styles-manager-style-detail"
        titleId="styles-manager-style-detail-title"
        styleName={styleNameDraft}
        readOnlyStyleName={activeStyle.source === "story_bible"}
        onStyleNameChange={setStyleNameDraftValue}
        onStyleNameBlur={() => saveSafely(saveStyleName())}
        instructions={instructionsDraft}
        instructionsWordLimit={getStyleGuideWordLimit(activeStyle.instructions ?? "")}
        simpleControls={simpleControls}
        onSimpleControlsChange={(controls) => {
          const next = normalizeSimpleStyleControls(controls);
          simpleControlsRef.current = next;
          simpleControlsEditedRef.current = true;
          setSimpleControls(next);
          setInstructionsDraftValue(getCurrentInstructionsDraft());
          saveSafely(saveInstructions(getCurrentInstructionsDraft(), { controls: next, syncLocalState: false }));
        }}
        instructionsRef={instructionsTextareaRef}
        onInstructionsChange={setInstructionsDraftValue}
        onInstructionsBlur={(value) => saveSafely(saveInstructions(value))}
        activeTab={activeDetailTab}
        onTabSelect={scrollToDetailSection}
        mainRef={detailMainRef}
        instructionsSectionRef={detailInstructionsSectionRef}
        writingSampleSectionRef={detailWritingSampleSectionRef}
        onBack={saveInstructionsAndBack}
        onClose={saveInstructionsAndClose}
        onCopyInstructions={copyInstructions}
        onGenerateSample={generateSamplePreview}
        generateSampleModelOptions={STYLE_GUIDE_GENERATE_SAMPLE_MODEL_OPTIONS}
        generateSampleModel={generateSampleWriteModel}
        onGenerateSampleModelChange={updateGenerateSampleWriteModel}
        isSaving={isSavingStyle}
        isGeneratingSample={isGeneratingSample}
        generatedSample={generatedSample}
        generateSampleError={generateSampleError}
        saveStyleError={saveStyleError}
        backLabel="Back to styles"
        closeLabel="Close settings"
        generateDisabled={!composeSimpleStyleInstructions(getCurrentInstructionsDraft(), simpleControls)}
        mainTestId="styles-manager-style-detail-main"
        instructionsSectionTestId="styles-manager-instructions-section"
        writingSampleSectionTestId="styles-manager-writing-sample-section"
        generatedSampleTestId="styles-manager-generated-sample-preview"
      />,
    );
  }

  if (view === "featured") {
    return renderStyleDetail(
      <StyleDetailOverlay data-testid="styles-manager-featured-detail">
        <StyleDetailHeader>
          <StyleDetailTitleGroup>
            <DetailIconButton
              type="button"
              aria-label="Back to styles"
              onClick={() => setView("list")}
            >
              <InlineSVG src={`${ASSET_ROOT}/settings-back-24.svg`} aria-hidden="true" />
            </DetailIconButton>
            <InlineSVG className="detail-style-icon" src={`${ASSET_ROOT}/settings-style-32.svg`} aria-hidden="true" />
            <h2 className="featured-style-title">{activeStyle.name}</h2>
            {activeStyle.tag && <SudowriteTag>{activeStyle.tag}</SudowriteTag>}
          </StyleDetailTitleGroup>
          <DetailCloseButton type="button" aria-label="Close settings" onClick={onClose}>
            <InlineSVG src={`${ASSET_ROOT}/settings-close-32.svg`} aria-hidden="true" />
          </DetailCloseButton>
        </StyleDetailHeader>
        <StyleDetailBody>
          <StyleDetailNav>
            <StyleDetailNavButton
              type="button"
              $active={activeDetailTab === "instructions"}
              aria-current={activeDetailTab === "instructions" ? "true" : undefined}
              onClick={() => scrollToDetailSection("instructions")}
            >
              <InlineSVG src={`${ASSET_ROOT}/settings-check-circle-24.svg`} aria-hidden="true" />
              <span>Instructions</span>
            </StyleDetailNavButton>
            <StyleDetailNavButton
              type="button"
              $active={activeDetailTab === "writingSample"}
              aria-current={activeDetailTab === "writingSample" ? "true" : undefined}
              onClick={() => scrollToDetailSection("writingSample")}
            >
              <InlineSVG src={`${ASSET_ROOT}/settings-write-24.svg`} aria-hidden="true" />
              <span>Writing Sample</span>
            </StyleDetailNavButton>
          </StyleDetailNav>
          <StyleDetailMain ref={detailMainRef} data-testid="styles-manager-featured-detail-main">
            <div ref={detailInstructionsSectionRef} data-testid="styles-manager-featured-instructions-section">
              <FeaturedStyleNotice>This style was created by Sudowrite and cannot be edited.</FeaturedStyleNotice>
              <StyleSectionHeading>
                <InlineSVG src={`${ASSET_ROOT}/settings-check-circle-32.svg`} aria-hidden="true" />
                <span>Instructions</span>
              </StyleSectionHeading>
              <FeaturedInstructions>
                {activeStyle.instructions || activeStyle.description}
              </FeaturedInstructions>
            </div>
            <DetailDivider />
            <div ref={detailWritingSampleSectionRef} data-testid="styles-manager-featured-writing-sample-section">
              <StyleSectionHeading>
                <InlineSVG src={`${ASSET_ROOT}/settings-write-32.svg`} aria-hidden="true" />
                <span>Writing Sample</span>
              </StyleSectionHeading>
              <WritingSample>
                {activeStyle.writingSample ||
                  `Jacques leapt over a chasm as the floor crumbled beneath his feet, ancient staff clutched tightly in his white-knuckled grip. The pyramid rumbled ominously.

"Not today," he muttered, hitting the ground running.

A glint of metal caught his eye. He ducked, feeling the whoosh of air as a blade trap swung inches above his head.
Scarabs skittered across the floor in waves, their carapaces glinting in the dim light. Jacques bounded from stone to stone, desperate to avoid the writhing mass below.

"Bloody bugs!"

He vaulted over a fallen pillar, rolling as he landed. The staff clattered against stone. For a heart-stopping moment, it slipped from his grasp. Jacques snatched it back, scrambling to his feet.

A colossal stone block crashed down behind him, sending tremors through the chamber. Dust and debris rained from above.

Jacques sprinted toward a sliver of daylight ahead, legs pumping furiously. He could feel the heat of the scarabs nipping at his heels.

"Come on, come on!"`}
              </WritingSample>
            </div>
          </StyleDetailMain>
        </StyleDetailBody>
      </StyleDetailOverlay>,
    );
  }

  if (generateStyleModalOpen) {
    return renderStyleDetail(
      <AddNewStyleModal
        mode="embedded"
        projectId={projectId}
        onClose={() => setGenerateStyleModalOpen(false)}
        onSkip={() => {
          setGenerateStyleModalOpen(false);
          void openBlankStyle();
        }}
        onContinueWithSample={(sample) => {
          setStyleMatcherInitialSample(sample);
          setGenerateStyleModalOpen(false);
          setStyleMatcherOpen(true);
        }}
        onPasteText={() => {
          setStyleMatcherInitialSample("");
          setGenerateStyleModalOpen(false);
          setStyleMatcherOpen(true);
        }}
      />,
    );
  }

  return (
    <>
      <StylesManagerPane data-testid="styles-manager-section">
        <StylesManagerHeader>
          <StylesSearchArea>
            <StylesManagerTitle $searchOpen={searchOpen} aria-hidden={searchOpen}>
              <InlineSVG src={`${ASSET_ROOT}/settings-style-32.svg`} aria-hidden="true" />
              <h3>Styles</h3>
            </StylesManagerTitle>
            <StylesSearchHeader $searchOpen={searchOpen}>
              <SearchButton
                ref={searchButtonRef}
                type="button"
                aria-label="Search styles"
                aria-hidden={searchOpen}
                disabled={searchOpen}
                onClick={openSearch}
              >
                <InlineSVG src="/assets/search.svg" aria-hidden="true" />
              </SearchButton>
              {searchOpen && (
                <DismissableLayer
                  asChild
                  onEscapeKeyDown={(event) => {
                    // Close a row menu first, then search, without dismissing Settings.
                    event.preventDefault();
                    if (addMenuOpen) {
                      setAddMenuOpen(false);
                    } else {
                      closeSearch();
                    }
                  }}
                >
                  <SearchControls>
                    <SearchInput
                      ref={searchInputRef}
                      aria-label="Search styles"
                      placeholder="Search styles"
                      value={searchQuery}
                      onChange={(event) => setSearchQuery(event.target.value)}
                    />
                    <SearchCloseButton
                      type="button"
                      aria-label={searchQuery ? "Clear style search" : "Close style search"}
                      onClick={clearOrCloseSearch}
                    >
                      <InlineSVG src={`${ASSET_ROOT}/settings-close-24.svg`} aria-hidden="true" />
                    </SearchCloseButton>
                  </SearchControls>
                </DismissableLayer>
              )}
            </StylesSearchHeader>
          </StylesSearchArea>
          <AddButtonWrap ref={addMenuRootRef}>
            <AddButton
              type="button"
              aria-haspopup="menu"
              aria-expanded={addMenuOpen}
              onClick={toggleAddMenu}
            >
              <InlineSVG src={`${ASSET_ROOT}/settings-add-24.svg`} aria-hidden="true" />
              <span>Add</span>
            </AddButton>
            {addMenuOpen && (
              <AddMenu role="menu" data-testid="styles-manager-add-menu">
                <MenuItem role="menuitem" type="button" onClick={openBlankStyle}>
                  <InlineSVG src={`${ASSET_ROOT}/settings-style-24.svg`} aria-hidden="true" />
                  <span>Blank Style</span>
                </MenuItem>
                <MenuItem role="menuitem" type="button" onClick={openGenerateStyle}>
                  <InlineSVG src={`${ASSET_ROOT}/settings-ai-24.svg`} aria-hidden="true" />
                  <span>Generate Style</span>
                </MenuItem>
              </AddMenu>
            )}
          </AddButtonWrap>
        </StylesManagerHeader>
        {stylesLoadState === "error" && (
          <StylesLoadStatus role="status">
            Unable to load your saved styles. Showing default Sudowrite styles.
          </StylesLoadStatus>
        )}
        <StylesTable>{renderStylesTableContent()}</StylesTable>
        {styleMatcherOpen && (
          <>
            <StyleMatcherLayerFix />
            <StyleMatcher
              close={() => setStyleMatcherOpen(false)}
              setError={toast?.setToast ?? (() => undefined)}
              setStyle={saveGeneratedStyle}
              project={project}
              initialSample={styleMatcherInitialSample}
              variant="styleGuide"
            />
          </>
        )}
      </StylesManagerPane>
    </>
  );
}

const StyleMatcherLayerFix = createGlobalStyle`
  .ReactModal__Overlay {
    z-index: 1000000000 !important;
  }

  .ReactModal__Content {
    z-index: 1000000001 !important;
  }
`;

const StylesManagerPane = styled.section`
  position: relative;
  width: 100%;
  min-height: 100%;
  padding: 24px 24px ${STYLE_ROW_MENU_BOTTOM_SPACE}px 0;
  box-sizing: border-box;
  color: var(--grey-0);
  font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  font-optical-sizing: none;
`;

const StylesManagerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 38px;
`;

const StylesSearchArea = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  min-width: 0;
  height: 40px;
  align-items: center;
`;

const SearchButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 32px;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--grey-0);
  cursor: pointer;

  &:disabled {
    cursor: default;
  }

  svg {
    width: 24px;
    height: 24px;
  }

  svg path {
    fill: currentColor !important;
  }

  svg [opacity] {
    opacity: 1;
  }
`;

const StylesSearchHeader = styled.div<{ $searchOpen: boolean }>`
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  width: ${({ $searchOpen }) => ($searchOpen ? "100%" : "32px")};
  min-width: 0;
  align-items: center;
  gap: 8px;
  height: 40px;
  background: var(--menu-color);
  border-bottom: 1px solid ${({ $searchOpen }) => ($searchOpen ? "var(--primary-cta)" : "transparent")};
  transition: width 280ms cubic-bezier(0.22, 1, 0.36, 1);

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  svg {
    width: 24px;
    height: 24px;
  }

  svg path {
    fill: currentColor !important;
  }

  svg [opacity] {
    opacity: 1;
  }
`;

const SearchControls = styled.div`
  display: grid;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  grid-template-columns: minmax(0, 1fr) 32px;
  align-items: center;
  column-gap: 16px;
`;

const SearchInput = styled.input`
  width: 100%;
  min-width: 0;
  height: 40px;
  padding: 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: var(--grey-0);
  font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
`;

const SearchCloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--grey-0);
  cursor: pointer;

  svg {
    width: 24px;
    height: 24px;
  }

  svg path {
    fill: currentColor !important;
  }
`;

const StylesManagerTitle = styled.div<{ $searchOpen: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 9px;
  margin-left: 7px;
  opacity: ${({ $searchOpen }) => ($searchOpen ? 0 : 1)};
  transition: opacity 180ms ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  svg {
    width: 32px;
    height: 32px;
    flex: 0 0 32px;
  }

  svg path {
    fill: currentColor !important;
  }

  h3 {
    margin: 0;
    color: var(--grey-0);
    font-size: 24px;
    font-weight: 500;
    line-height: 29px;
  }
`;

const AddButtonWrap = styled.div`
  position: relative;
`;

const AddButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 9px;
  height: 40px;
  min-width: 96px;
  padding: 0 16px;
  border: 0 !important;
  border-radius: 8px !important;
  background: var(--primary-cta) !important;
  color: var(--cta-text-color) !important;
  font-size: 16px !important;
  font-weight: 500 !important;
  line-height: 24px !important;
  cursor: pointer;

  svg {
    width: 24px;
    height: 24px;
  }

  svg path {
    fill: currentColor !important;
  }
`;

const AddMenu = styled.div`
  position: absolute;
  top: 48px;
  right: 0;
  z-index: 3;
  width: 190px;
  padding: 4px 0;
  border-radius: 8px;
  background: var(--menu-color);
  box-shadow: var(--default-drop-shadow);
`;

const MenuItem = styled.button<{ $danger?: boolean; $italic?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 40px;
  padding: 0 14px;
  border: 0;
  background: transparent;
  color: ${({ $danger }) => ($danger ? "var(--danger-color)" : "var(--grey-0)")} !important;
  text-align: left;
  font-size: 16px !important;
  font-style: ${({ $italic }) => ($italic ? "italic" : "normal")} !important;
  font-weight: 500 !important;
  line-height: 21px !important;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: var(--menu-color-hover);
    outline: none;
  }

  svg {
    width: 24px;
    height: 24px;
    flex: 0 0 24px;
  }

  svg path {
    fill: currentColor !important;
  }
`;

const StylesTable = styled.div`
  width: 100%;
  border: 1px solid var(--grey-3);
  border-radius: 8px;
  background: var(--menu-color);
`;

const StylesLoadStatus = styled.p`
  margin: -24px 0 16px;
  color: var(--grey-1);
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
`;

const StylesTableStatus = styled.div`
  display: flex;
  align-items: center;
  min-height: 56px;
  padding: 0 24px;
  color: var(--grey-1);
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
`;

const StyleManagerRow = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  column-gap: 16px;
  align-items: center;
  width: 100%;
  height: 56px;
  padding: 0 16px 0 24px;
  border: 0;
  border-bottom: 1px solid var(--grey-3);
  background: var(--menu-color);
  color: var(--grey-0);
  text-align: left;
  cursor: pointer;

  &:first-child {
    border-top-left-radius: 7px;
    border-top-right-radius: 7px;
  }

  &:last-child {
    border-bottom: 0;
    border-bottom-left-radius: 7px;
    border-bottom-right-radius: 7px;
  }

  &:hover,
  &:focus-visible {
    background: var(--menu-color-hover);
    outline: none;
  }

`;

const RowName = styled.span`
  min-width: 0;
  color: inherit;
  font-size: 16px;
  font-weight: 500;
  line-height: 19.3636px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transform: translateY(1px);
`;

const SudowriteTag = styled.span`
  display: inline-flex;
  align-items: center;
  justify-self: start;
  height: 24px;
  padding: 0 5px;
  border-radius: 6px;
  background: var(--selected-element-background);
  color: var(--selected-element-text);
  font-size: 12px;
  font-weight: 400;
  line-height: 15px;
`;

const RowActions = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  grid-column: 3;
  justify-self: end;
`;

const RowIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--grey-0);
  cursor: pointer;

  svg {
    width: 24px;
    height: 24px;
  }

  svg path {
    fill: currentColor !important;
  }
`;

const RowEditButton = styled(RowIconButton)`
  pointer-events: none;

  /* Override the Settings shell's button opacity reset. */
  && {
    opacity: 0;
  }

  ${StyleManagerRow}:hover &,
  ${StyleManagerRow}:focus-within & {
    opacity: 1;
    pointer-events: auto;
    animation: ${popInKeyframes} 250ms ease forwards;
  }

  @media (hover: none) {
    && {
      opacity: 1;
      pointer-events: auto;
    }
  }

  @media (hover: none), (prefers-reduced-motion: reduce) {
    &&& {
      animation: none;
    }
  }
`;

const StyleDetailOverlay = styled.section`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 2;
  overflow: hidden;
  border-radius: 20px;
  background: var(--menu-color);
  color: var(--grey-0);
  text-align: left;
  font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  font-optical-sizing: none;
`;

const StyleDetailHeader = styled.header`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  height: 72px;
  padding: 0 24px;
  box-sizing: border-box;

  &::after {
    content: "";
    position: absolute;
    left: 24px;
    right: 24px;
    bottom: 0;
    height: 1px;
    background: var(--grey-3);
  }
`;

const StyleDetailTitleGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 16px;
  flex: 1 1 auto;
  min-width: 0;

  .detail-style-icon {
    width: 32px;
    height: 32px;
    flex: 0 0 32px;
  }

  .detail-style-icon path {
    fill: currentColor !important;
  }

  h2 {
    flex: 1 1 auto;
    margin: 0;
    color: var(--grey-0);
    font-size: 24px;
    font-weight: 500;
    line-height: 29px;
    min-width: 0;
  }

  .featured-style-title {
    width: auto;
    min-width: 0;
    flex: 0 1 auto;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  ${SudowriteTag} {
    flex-shrink: 0;
  }
`;

const DetailIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--grey-0);
  cursor: pointer;

  svg {
    width: 32px;
    height: 32px;
  }

  svg path {
    fill: currentColor !important;
  }
`;

const DetailCloseButton = styled(DetailIconButton)`
  width: 32px;
  height: 32px;

  svg {
    width: 32px;
    height: 32px;
  }
`;

const StyleDetailBody = styled.div`
  display: grid;
  grid-template-columns: 246px 1fr;
  height: calc(100% - 72px);
  min-height: 0;
`;

const StyleDetailNav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  box-sizing: border-box;
`;

const StyleDetailNavButton = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: 206px;
  height: 40px;
  padding: 0 12px;
  border: 0;
  border-radius: 8px;
  background: ${({ $active }) => ($active ? "var(--selected-element-background)" : "transparent")};
  color: ${({ $active }) => ($active ? "var(--selected-element-text)" : "var(--grey-0)")};
  font-size: 16px;
  font-weight: 500;
  line-height: 19.3636px;
  text-align: left;
  cursor: pointer;

  svg {
    width: 24px;
    height: 24px;
    flex: 0 0 24px;
  }

  svg path {
    fill: currentColor !important;
  }
`;

const StyleDetailMain = styled.div`
  padding: 24px;
  box-sizing: border-box;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
`;

const StyleSectionHeading = styled.h3`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 24px;
  color: var(--grey-0);
  font-size: 20px;
  font-weight: 500;
  line-height: 24px;

  svg {
    width: 32px;
    height: 32px;
    flex: 0 0 32px;
  }

  svg path {
    fill: currentColor !important;
  }
`;

const DetailDivider = styled.hr`
  width: 706px;
  height: 1px;
  margin: 40px 0 39px;
  border: 0;
  background: var(--grey-3);
`;

const FeaturedInstructions = styled.p`
  width: 706px;
  margin: 0;
  color: var(--grey-0);
  font-size: 16px;
  font-weight: 600;
  line-height: 19px;
`;

const FeaturedStyleNotice = styled.p`
  margin: 0 0 40px;
  color: var(--grey-0);
  font-size: 16px;
  font-style: italic;
  line-height: 24px;
`;

const WritingSample = styled.div`
  width: 706px;
  color: var(--grey-0);
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  white-space: pre-wrap;
`;
